import React, { useEffect } from "react";

import { useFormContext } from "react-hook-form";
import { CustomTitleLabel } from "./CustomBaseComponments";
import CustomRadios from "components/base/CustomRadios";
import InputFieldControl from "components/base/InputFieldControl";
import { EarningRuleRewardType } from "config/CustomEnums";
import ReactHookFormNumberMutipleInputWithUnit from "components/reactHookFormComponents/ReactHookFormNumberMutipleInputWithUnit";
import { REWARD_LOGIC_TYPE } from "./RewardLogicSelector";
import { hasError } from "components/base/ErrorFieldMessage";

export const REWARD_AMOUNT_TYPE = {
  oneOff: "ONE_OFF",
  dynamic: "DYNAMIC",
}
export const REWARD_AMOUNT_BASED_TYPE = {
  totalValue: "TOTAL_VALUE",
  allItems: "ALL_ITEMS",
  eachItem: "EACH_ITEM",
}

export const DISPLAY_REWARD_AMOUNT_BASED_TYPE = {
  [REWARD_AMOUNT_BASED_TYPE.totalValue]: "Based on total transaction spending",
  [REWARD_AMOUNT_BASED_TYPE.allItems]: "Based on spending from all qualified-line-items",
  [REWARD_AMOUNT_BASED_TYPE.eachItem]: "Based on spending from each qualified-line-item",
}

const RewardAmountInputArea = ({
  prefix="",
}) => {
  const { watch, setValue, formState: { errors } } = useFormContext();
  const rewardLogicTypeField = `${prefix}rewardLogicType`;
  const rewardAmountTypeField = `${prefix}rewardAmountType`;
  const rewardAmountBasedTypeField = `${prefix}rewardAmountBasedType`;

  const watchRewardLogicType = watch(rewardLogicTypeField);
  const watchRewardAmountType = watch(rewardAmountTypeField)
  const watchRewardAmountBasedType = watch(rewardAmountBasedTypeField)
  const watchLineItemRequirement = watch(`${prefix}lineItemRequirement`);
  const linkedStampCampaign = watch(`${prefix}linkedStampCampaign`);
  const rewardType = watch(`${prefix}rewardType`);

  useEffect(() => {
    if (!watchRewardAmountType) {
      setValue(rewardAmountTypeField, REWARD_AMOUNT_TYPE.oneOff, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnTransactionValue
      && watchRewardAmountType === REWARD_AMOUNT_TYPE.dynamic
      && !watchRewardAmountBasedType
    ) {
      setValue(rewardAmountBasedTypeField, REWARD_AMOUNT_BASED_TYPE.totalValue, { shouldDirty: true })
      return
    }
    if (
      watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity
      && watchRewardAmountType === REWARD_AMOUNT_TYPE.dynamic
      && ![REWARD_AMOUNT_BASED_TYPE.allItems, REWARD_AMOUNT_BASED_TYPE.eachItem].includes(watchRewardAmountBasedType)
    ) {
      setValue(rewardAmountBasedTypeField, REWARD_AMOUNT_BASED_TYPE.allItems, { shouldDirty: true })
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRewardLogicType, watchRewardAmountType])

  const getTransactionValueBasedRewardAmountInputConfig = () => {
    if (watchRewardAmountType === REWARD_AMOUNT_TYPE.oneOff) {
      if (linkedStampCampaign) {
        return [
          {
            component: InputFieldControl,
            componentProps: {
              type: "number",
              unit: "stamps",
              name: `${prefix}stampTypeRewardPerXValue`,
              useDefaultFieldName: true,
              useDefaultValue: false,
              customClass: "custom-number-input-short margin-right-10-percent",
            }
          },
        ]
      }
      if (rewardType === EarningRuleRewardType.points) {
        return [
          {
            component: InputFieldControl,
            componentProps: {
              type: "number",
              unit: "points",
              name: `${prefix}pointTypeRewardPerXValue`,
              useDefaultFieldName: true,
              useDefaultValue: false,
              customClass: `custom-number-input-short margin-right-10-percent ${hasError(errors, 'tpeTypeRewardPerXValue') ? "error-field" : ""}`,
            }
          },
          {
            component: InputFieldControl,
            componentProps: {
              type: "number",
              unit: "progression points",
              name: `${prefix}tpeTypeRewardPerXValue`,
              useDefaultFieldName: true,
              useDefaultValue: false,
              customClass: "custom-number-input-short margin-right-10-percent",
            }
          },
        ]
      }
      return [
        {
          component: InputFieldControl,
          componentProps: {
            type: "number",
            unit: "coupons",
            name: `${prefix}couponTypeRewardPerXValue`,
            useDefaultFieldName: true,
            useDefaultValue: false,
            customClass: "custom-number-input-short margin-right-10-percent",
          }
        },
      ]
    }
    if (watchRewardAmountType === REWARD_AMOUNT_TYPE.dynamic) {
      if (linkedStampCampaign) {
        return [
          {
            component: ReactHookFormNumberMutipleInputWithUnit,
            componentProps: {
              type: "number",
              numberTitle: "HK$",
              per: "=",
              unit: "stamps",
              firstValueFieldName: `${prefix}stampTypeRewardPerX`,
              secondValueFieldName: `${prefix}stampTypeRewardPerXValue`,
              errorId: `${prefix}dynamicStampReward`,
            },
          },
        ]
      }
      if (rewardType === EarningRuleRewardType.points) {
        return [
          {
            component: ReactHookFormNumberMutipleInputWithUnit,
            componentProps: {
              type: "number",
              numberTitle: "HK$",
              per: "=",
              unit: "points",
              firstValueFieldName: `${prefix}pointTypeRewardPerX`,
              secondValueFieldName: `${prefix}pointTypeRewardPerXValue`,
              errorId: "dynamicPointReward",
            },
          },
          {
            component: ReactHookFormNumberMutipleInputWithUnit,
            componentProps: {
              type: "number",
              numberTitle: "HK$",
              per: "=",
              unit: "progression points",
              firstValueFieldName: `${prefix}tpeTypeRewardPerX`,
              secondValueFieldName: `${prefix}tpeTypeRewardPerXValue`,
              errorId: "dynamicTpeReward",
            },
          },
        ]
      }
      return [
        {
          component: ReactHookFormNumberMutipleInputWithUnit,
          componentProps: {
            type: "number",
            numberTitle: "HK$",
            per: "=",
            unit: "coupons",
            firstValueFieldName: `${prefix}couponTypeRewardPerX`,
            secondValueFieldName: `${prefix}couponTypeRewardPerXValue`,
            errorId: `${prefix}dynamicCouponReward`,
          },
        },
      ]
    }
  }
  const getItemQuantityBasedRewardAmountInputConfig = () => {
    if (watchRewardAmountType === REWARD_AMOUNT_TYPE.oneOff) {
      if (linkedStampCampaign) {
        return [
          {
            component: InputFieldControl,
            componentProps: {
              unit: "stamps",
              name: `${prefix}stampTypeRewardPerXValue`,
              useDefaultFieldName: true,
              useDefaultValue: false,
              customClass: "custom-number-input-short margin-right-10-percent",
            }
          },
        ]
      }
      if (rewardType === EarningRuleRewardType.points) {
        return [
          {
            component: InputFieldControl,
            componentProps: {
              unit: "points",
              name: `${prefix}pointTypeRewardPerXValue`,
              useDefaultFieldName: true,
              useDefaultValue: false,
              customClass: "custom-number-input-short margin-right-10-percent",
            }
          },
          {
            component: InputFieldControl,
            componentProps: {
              unit: "progression points",
              name: `${prefix}tpeTypeRewardPerXValue`,
              useDefaultFieldName: true,
              useDefaultValue: false,
              customClass: "custom-number-input-short margin-right-10-percent",
            }
          },
        ]
      }
      return [
        {
          component: InputFieldControl, componentProps: {
            unit: "coupons",
            name: `${prefix}couponTypeRewardPerXValue`,
            useDefaultFieldName: true,
            useDefaultValue: false,
            customClass: "custom-number-input-short margin-right-10-percent",
          }
        },
      ]
    }
    if (watchRewardAmountType === REWARD_AMOUNT_TYPE.dynamic) {
      if (linkedStampCampaign) {
        return [
          {
            component: ReactHookFormNumberMutipleInputWithUnit,
            componentProps: {
              numberTitle: "Every",
              per: "number of item =",
              unit: "stamps",
              firstValueFieldName: `${prefix}stampTypeRewardPerX`,
              secondValueFieldName: `${prefix}stampTypeRewardPerXValue`,
              errorId: `${prefix}dynamicStampReward`,
            },
          },
        ]
      }
      if (rewardType === EarningRuleRewardType.points) {
        return [
          {
            component: ReactHookFormNumberMutipleInputWithUnit,
            componentProps: {
              numberTitle: "Every",
              per: "number of item =",
              unit: "points",
              firstValueFieldName: `${prefix}pointTypeRewardPerX`,
              secondValueFieldName: `${prefix}pointTypeRewardPerXValue`,
              errorId: "dynamicPointReward",
            },
          },
          {
            component: ReactHookFormNumberMutipleInputWithUnit,
            componentProps: {
              numberTitle: "Every",
              per: "number of item =",
              unit: "progression points",
              firstValueFieldName: `${prefix}tpeTypeRewardPerX`,
              secondValueFieldName: `${prefix}tpeTypeRewardPerXValue`,
              errorId: "dynamicTpeReward",
            },
          },
        ]
      }
      return [
        {
          component: ReactHookFormNumberMutipleInputWithUnit,
          componentProps: {
            numberTitle: "Every",
            per: "number of item =",
            unit: "coupons",
            firstValueFieldName: `${prefix}couponTypeRewardPerX`,
            secondValueFieldName: `${prefix}couponTypeRewardPerXValue`,
            errorId: `${prefix}dynamicCouponReward`,
          },
        },
      ]
    }
  }

  const getRewardAmountInputConfig = () => {
    if (watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnTransactionValue || !watchLineItemRequirement) {
      return getTransactionValueBasedRewardAmountInputConfig()
    }
    if (watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity) {
      return getItemQuantityBasedRewardAmountInputConfig()

    }
  }

  const getDisplayTitle = () => {
    if (!watchLineItemRequirement) {
      return "Reward quantity base on total transaction spending (total transaction value)"
    }
    if (watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity) {
      return "Reward quantity based on the total quantity of the qualified-line-item"
    }
    if (watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnTransactionValue) {
      return "Reward quantity"
    }
  }

  const getRewardAmountOptions = () => {
    if (watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity) {
      return [
        {
          label: 'Based on quantity of all qualified-line-items',
          value: REWARD_AMOUNT_BASED_TYPE.allItems,
        },
        {
          label: 'Based on quantity of each qualified-line-item',
          value: REWARD_AMOUNT_BASED_TYPE.eachItem,
        },
      ]
    }
    return [
      {
        label: DISPLAY_REWARD_AMOUNT_BASED_TYPE[REWARD_AMOUNT_BASED_TYPE.totalValue],
        value: REWARD_AMOUNT_BASED_TYPE.totalValue,
        popoverTips: (
          `Total transaction value of the transaction

                  Scenario 
                  Item A = $1.5 each
                  Item B = $5 each
                  Item C = $2.5 each

                  Transaction:
                  Item A x 2 ----------$3
                  Item B x 3 ----------$15
                  Item C x 2 ----------$5
                  Eligible total: $23

                  Reward
                  HK$2 = 1 coupon
                  Final rewards 
                  = 11 coupons`),
      },
      {
        label: DISPLAY_REWARD_AMOUNT_BASED_TYPE[REWARD_AMOUNT_BASED_TYPE.allItems],
        value: REWARD_AMOUNT_BASED_TYPE.allItems,
        popoverTips: (
          `Total spending from all qualified-line-items

                  Scenario 
                  Item A = $1.5 each
                  Item B = $5 each
                  Item C = $2.5 each

                  Transaction:
                  Item A x 2 ---------- $3
                  Item B x 3 ---------- $15
                  Item C x 2 ---------- $5
                  Eligible total: $23

                  Required line item: item A & C
                  Reward
                  HK$2 = 1 coupon
                  Final rewards 
                  = $3 + $5 = $8
                  = 4 coupons`),
      },
      {
        label: DISPLAY_REWARD_AMOUNT_BASED_TYPE[REWARD_AMOUNT_BASED_TYPE.eachItem],
        value: REWARD_AMOUNT_BASED_TYPE.eachItem,
        popoverTips: (
          `Spending from each qualified-line-items

                  Scenario 
                  Item A = $1.5 each
                  Item B = $5 each
                  Item C = $2.5 each

                  Transaction:
                  Item A x 2 ---------- $3
                  Item B x 3 ---------- $15
                  Item C x 2 ---------- $5
                  Eligible total: $23

                  Required line item: item A & C
                  Reward
                  HK$2 = 1 coupon
                  Final rewards 
                  Item A $3 = 1 coupon
                  Item C $5 = 2 coupon
                = 3 coupons`),
      },
    ]
  }
  return <>
    <CustomTitleLabel
      title={getDisplayTitle()}
    />
    <div className="d-flex flex-column flex-gap-8">
      <div className="d-flex">
        <CustomRadios
          default={watchRewardAmountType}
          onChange={(value) => {
            setValue(rewardAmountTypeField, value, { shouldDirty: true })
          }}
          options={[
            { label: 'One-off fixed quantity', value: REWARD_AMOUNT_TYPE.oneOff },
            {
              label: 'Dynamic',
              value: REWARD_AMOUNT_TYPE.dynamic,
              popoverTips: watchLineItemRequirement && watchRewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity && watchRewardAmountType === REWARD_AMOUNT_TYPE.dynamic
                ?
                `Coupons or stamps are issued based on either the qualified line item quantity in a transaction. Any remaining quantity that does not meet the single reward required amount, does not qualify for additional rewards.
                e.g. For every 2 item A, you earn 1 coupon. For a transaction with 5 item A, you earn 2 coupons, as the remaining 1 quantity does not qualify for an additional coupon.`
                : "Coupons or stamps are issued based on either the total spending, spending from all per line item or the per line item price." +
                " Any remaining amount that does not meet the single reward required amount, does not qualify for additional rewards.\n" +
                "e.g.For every $2 spent, you earn 1 coupon.For a $5 transaction, you earn 2 coupons, as the remaining $1 does not qualify for an additional coupon."
            },
          ]}
        />
      </div>
      {watchLineItemRequirement && watchRewardAmountType === REWARD_AMOUNT_TYPE.dynamic &&
        <div className="d-flex">
          <CustomRadios
            default={watchRewardAmountBasedType}
            onChange={(value) => {
              setValue(rewardAmountBasedTypeField, value, { shouldDirty: true })
            }}
            options={getRewardAmountOptions()}
          />
        </div>
      }
      {<div className="d-flex flex-column flex-gap-8">
        {getRewardAmountInputConfig()?.map(config => (
          config?.component &&
          <config.component
            {...config?.componentProps}
          />
        ))}
      </div>}
    </div>
  </>
}


export default RewardAmountInputArea;